import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';
import './ClassSettingsHelpModal.css';

const ClassSettingsHelpModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState([]);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!isOpen) setExpandedSections([]);
  }, [isOpen]);

  const toggleSection = (sectionId) => {
    const isExpanding = !expandedSections.includes(sectionId);
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    if (isExpanding) {
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
  };

  if (!isOpen) return null;

  const sections = [
    {
      id: 'school-url',
      title: '1. Your school links and how to share them',
      content: (
        <>
          <p><strong>What is your school URL?</strong> Your school URL is a unique address where prospective students can find your public class pages (for example, <code>dancelocale.com/school/dance-studio-salsa</code>). It is the base for all your public landing pages.</p>
          <p><strong>Setting your URL:</strong> Choose a short, memorable custom URL (e.g., <code>salsa-studio-sydney</code>). It can only contain lowercase letters, numbers, and hyphens. Once set, changing it will break any links you have already shared, so choose carefully.</p>
          <p><strong>Two links to share:</strong></p>
          <ul>
            <li><strong>School link (always shows your current term):</strong> the main link. Leave it on your website, Instagram bio, or newsletter and never touch it again. It always shows whatever term is running now, so it updates itself as terms roll over.</li>
            <li><strong>Link to a specific term (optional):</strong> promoting a term that has not started yet? Pick it from the dropdown to get a link that takes visitors straight to that term. Only terms with enrolment open can be shared this way. Open enrolment for the term in the Terms section first if it does not appear.</li>
          </ul>
          <p><strong>If you close a shared term later:</strong> any specific-term link you have already shared keeps working. Visitors simply see a "this term isn't open for enrolment yet, check back soon" message and a General Enquiry option instead of the class list. The link never becomes a dead end.</p>
          <p><strong>How to share:</strong> Click the copy icon on either link and paste it into your email newsletters, Instagram bio, website, or WhatsApp messages.</p>
          <p><strong>Auto-generation:</strong> If you have not set a custom URL yet, we suggest one based on your studio name. You can edit it before saving.</p>
        </>
      )
    },
    {
      id: 'url-groups',
      title: '2. URL groups: creating focused pages for specific audiences',
      content: (
        <>
          <p><strong>Your school URL already shows everything.</strong> When someone visits your school URL, they see all your classes and short courses for the current term. No setup required.</p>
          <p><strong>Groups let you create focused pages.</strong> Use a group when you want to send a specific audience to a page showing only what's relevant to them. Examples: a page with just your Salsa classes, a page with just short courses, or a beginner-only page. Each group shows only the classes and short courses you assign to it.</p>
          <p><strong>How to assign classes to a group:</strong> In the URL Groups list, click the checklist icon on any group to open the assignment panel. Check the weekly classes and short courses you want to appear on that group's public page, then save.</p>
          <p><strong>A class can belong to multiple groups.</strong> No need to duplicate it. For example, a Salsa Beginners class can appear on both your "All Classes" group and your "Salsa" group.</p>
          <p><strong>Group URLs:</strong> Each group has a custom URL (e.g., <code>salsa</code>, <code>short-courses</code>). It auto-generates from the group name or you can set your own when creating the group.</p>
        </>
      )
    },
    {
      id: 'enrolment-flow',
      title: '3. How the public enrolment page works end-to-end',
      content: (
        <>
          <p><strong>Student journey:</strong></p>
          <ol>
            <li>Student clicks your shared group URL and lands on a branded page showing the classes in that group.</li>
            <li>They browse the schedule, levels, and times, then click "Enquire" or "Sign up".</li>
            <li>They fill in their name and experience. No account creation required.</li>
            <li>Their enquiry is saved and appears in <strong>Students → New Enquiries</strong> in your dashboard.</li>
          </ol>
          <p><strong>Admin follow-up:</strong> In the Students section, open "New Enquiries", review each enquiry, and assign the student to the appropriate class to convert them into an active enrolment. You can also send them a welcome email from there.</p>
          <p><strong>No payment on the public page:</strong> The public page is for discovery and sign-up intent only. Payment is handled separately via the Pricing and Payments section once the student is enrolled.</p>
        </>
      )
    },
    {
      id: 'trial-deals',
      title: '4. Trial deals: offering a first-class experience',
      content: (
        <>
          <p>
            A <strong>trial deal</strong> is a discounted or promotional price you offer for a first class.
            Create one from the Promote page to let new students try your school before committing to a full term.
          </p>
          <p>
            <strong>Creating a trial deal:</strong> Click <strong>Create trial deal</strong>, select the term the
            deal applies to, enter a name, price, and the classes students can attend. Only terms that are currently
            open for enrolment appear in the term selector — if your next term is not showing, open it for enrolment
            in the Terms section first.
          </p>
          <p><strong>Sharing your trial deal.</strong> Each trial deal has a dedicated public landing page. Use
            the share panel to get your link out via four channels:</p>
          <ul>
            <li><strong>Copy link:</strong> paste into emails, WhatsApp, or your website.</li>
            <li><strong>QR code:</strong> download and print for noticeboards or flyers.</li>
            <li><strong>Social share:</strong> share directly to your social platforms.</li>
            <li><strong>Embed code:</strong> add a purchase widget to your own website.</li>
          </ul>
          <p>
            <strong>Automatic prospect tracking.</strong> When a brand-new student purchases your trial deal,
            they are automatically added to the <strong>Prospects</strong> tab in your Students section. You
            can follow up, view their contact details, and enrol them into a full class when they are ready.
          </p>
          <p>
            <strong>Track performance.</strong> Each trial deal shows its sold count and total revenue both
            on the Promote page list and inside the edit modal, so you can see at a glance how each campaign is performing.
          </p>
        </>
      )
    }
  ];

  return (
    <div className="class-settings-help-overlay" onClick={onClose}>
      <div className="class-settings-help-modal" onClick={e => e.stopPropagation()}>
        <div className="class-settings-help-header">
          <div className="class-settings-help-header-content">
            <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
            <h2>Promote Help</h2>
          </div>
          <button className="class-settings-help-close" onClick={onClose}>×</button>
        </div>
        <div className="class-settings-help-body">
          {sections.map(section => (
            <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
              <button
                className="help-accordion-header"
                onClick={() => toggleSection(section.id)}
              >
                <Icon
                  path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                  size={0.8}
                  color="var(--dl-color-purple-default)"
                />
                <span>{section.title}</span>
              </button>
              {expandedSections.includes(section.id) && (
                <div className="help-accordion-content">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="class-settings-help-footer">
          <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
};

export default ClassSettingsHelpModal;
